import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Logo } from 'components/Logo';
import { useAuth } from 'contexts/AuthContext';
import {
  Home, Package, Map, MessageCircle, Wallet, User, Bell, LogOut,
  Menu, Truck, ClipboardList, BadgeCheck, Users, AlertTriangle, BarChart3,
  Plus, Search, Plane, CircleHelp, BellRing, Trash2, Mail, TicketPercent,
} from 'lucide-react';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { useLanguage } from 'lib/i18n';
import { LanguageSwitcher } from 'components/LanguageSwitcher';
import { useEffect, useRef, useState } from 'react';

const NAV_BY_ROLE = {
  sender: [
    { to: '/dashboard', label: 'nav.dashboard', icon: Home },
    { to: '/deliveries/new', label: 'nav.publishParcel', icon: Plus },
    { to: '/deliveries', label: 'nav.myParcels', icon: Package },
    { to: '/search?tab=trip', label: 'nav.search', icon: Search },
    { to: '/missions', label: 'nav.missions', icon: ClipboardList },
    { to: '/documents', label: 'nav.documents', icon: BadgeCheck },
    { to: '/messages', label: 'nav.messages', icon: MessageCircle },
    { to: '/wallet', label: 'nav.wallet', icon: Wallet },
    { to: '/profile', label: 'nav.profile', icon: User },
    { to: '/saved-searches', label: 'nav.alerts', icon: BellRing },
    { to: '/support', label: 'nav.support', icon: CircleHelp },
  ],
  traveler: [
    { to: '/dashboard', label: 'nav.dashboard', icon: Home },
    { to: '/trips/new', label: 'nav.publishTrip', icon: Plane },
    { to: '/trips', label: 'nav.myTrips', icon: Map },
    { to: '/search?tab=parcel', label: 'nav.searchParcels', icon: Search },
    { to: '/missions', label: 'nav.missions', icon: ClipboardList },
    { to: '/documents', label: 'nav.documents', icon: BadgeCheck },
    { to: '/messages', label: 'nav.messages', icon: MessageCircle },
    { to: '/wallet', label: 'nav.earnings', icon: Wallet },
    { to: '/profile', label: 'nav.profile', icon: User },
    { to: '/saved-searches', label: 'nav.alerts', icon: BellRing },
    { to: '/support', label: 'nav.support', icon: CircleHelp },
  ],
  deliverer: [
    { to: '/dashboard', label: 'nav.dashboard', icon: Home },
    { to: '/search?tab=parcel', label: 'nav.searchParcels', icon: Search },
    { to: '/missions', label: 'nav.missions', icon: ClipboardList },
    { to: '/vehicles', label: 'nav.vehicles', icon: Truck },
    { to: '/documents', label: 'nav.documents', icon: BadgeCheck },
    { to: '/messages', label: 'nav.messages', icon: MessageCircle },
    { to: '/wallet', label: 'nav.earnings', icon: Wallet },
    { to: '/profile', label: 'nav.profile', icon: User },
    { to: '/saved-searches', label: 'nav.alerts', icon: BellRing },
    { to: '/support', label: 'nav.support', icon: CircleHelp },
  ],
  admin: [
    { to: '/admin', label: 'nav.overview', icon: BarChart3 },
    { to: '/admin/users', label: 'nav.users', icon: Users },
    { to: '/admin/campaigns', label: 'nav.campaigns', icon: Mail },
    { to: '/admin/discount-codes', label: 'nav.discountCodes', icon: TicketPercent },
    { to: '/admin/deliveries', label: 'nav.parcels', icon: Package },
    { to: '/admin/trips', label: 'nav.trips', icon: Map },
    { to: '/admin/missions', label: 'nav.missions', icon: ClipboardList },
    { to: '/admin/disputes', label: 'nav.disputes', icon: AlertTriangle },
    { to: '/admin/reports', label: 'nav.reports', icon: AlertTriangle },
    { to: '/admin/support', label: 'nav.support', icon: CircleHelp },
    { to: '/admin/payouts', label: 'nav.transfers', icon: Wallet },
    { to: '/admin/audit-logs', label: 'nav.audit', icon: ClipboardList },
    { to: '/admin/validations', label: 'nav.identity', icon: BadgeCheck },
  ],
};

const pickRole = (user) => {
  if (!user) return 'sender';
  if (user.is_admin) return 'admin';
  const stored = localStorage.getItem('blablacolis_active_role');
  if (stored && (user.roles || []).includes(stored)) return stored;
  return (user.roles && user.roles[0]) || 'sender';
};

export const DashboardShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const notificationsMutation = useRef(false);
  const notificationsRequest = useRef(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [activeRole, setActiveRole] = useState(pickRole(user));

  useEffect(() => {
    setActiveRole(pickRole(user));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const loadNotifications = () => {
      if (notificationsMutation.current) return;
      const request = ++notificationsRequest.current;
      api.get('/notifications').then(r => {
        if (active && request === notificationsRequest.current) setNotifications(r.data);
      }).catch(() => {});
    };
    loadNotifications();
    const loadUnreadMessages = () => api.get('/messages/unread-count').then(r => setUnreadMessages(Number(r.data?.count || 0))).catch(() => {});
    loadUnreadMessages();
    const interval = setInterval(() => {
      loadNotifications();
      loadUnreadMessages();
    }, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [user]);

  const updateNotifications = async (action, notificationId) => {
    if (notificationsMutation.current) return;
    if (action === 'deleteAll' && !window.confirm(t('shell.deleteAllNotificationsConfirm'))) return;
    notificationsMutation.current = true;
    notificationsRequest.current += 1;
    setNotificationsBusy(true);
    try {
      if (action === 'deleteAll') {
        await api.delete('/notifications');
        setNotifications([]);
        toast.success(t('shell.notificationsDeleted'));
      } else if (action === 'delete') {
        await api.delete(`/notifications/${notificationId}`);
        setNotifications(current => current.filter(notification => notification.id !== notificationId));
      } else {
        await api.post('/notifications/read-all');
        setNotifications(current => current.map(notification => ({ ...notification, read: true })));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('shell.notificationsActionError')));
    } finally {
      notificationsMutation.current = false;
      setNotificationsBusy(false);
    }
  };

  useEffect(() => {
    if (!user || !location.pathname.startsWith('/messages')) return;
    setUnreadMessages(0);
    api.post('/messages/read-all').catch(() => {});
  }, [location.pathname, user]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const nav = NAV_BY_ROLE[activeRole] || NAV_BY_ROLE.sender;

  const activeNavPath = nav
    .filter((item) => {
      const itemPath = item.to.split('?')[0];
      return location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`);
    })
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;

  const switchRole = (role) => {
    localStorage.setItem('blablacolis_active_role', role);
    setActiveRole(role);
    navigate('/dashboard');
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="px-5 py-4 border-b">
        <Link to="/dashboard" onClick={() => setOpen(false)}>
          <Logo />
        </Link>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((item) => {
          const Icon = item.icon;
          const active = item.to === activeNavPath;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`sidebar-nav-${item.label.replace(/[^a-z0-9]+/gi, '-')}-link`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-secondary'
              }`}
            >
              <span className="relative">
                <Icon size={18} />
                {item.to === '/messages' && unreadMessages > 0 && (
                  <span className="absolute -right-3 -top-2 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center font-semibold" aria-label={`${unreadMessages} message${unreadMessages > 1 ? 's' : ''} non lu${unreadMessages > 1 ? 's' : ''}`}>
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </span>
              <span>{t(item.label)}</span>
            </Link>
          );
        })}
      </nav>
      {user && (user.roles || []).length > 1 && !user.is_admin && (
        <div className="px-3 py-3 border-t">
          <div className="text-xs text-muted-foreground mb-2 px-2">{t('shell.switchRole')}</div>
          <div className="flex flex-wrap gap-1">
            {(user.roles || []).map((r) => (
              <Button
                key={r}
                size="sm"
                variant={r === activeRole ? 'default' : 'outline'}
                onClick={() => switchRole(r)}
                data-testid={`role-switch-${r}-button`}
                className="text-xs h-7"
              >
                {t(`role.${r}`)}
              </Button>
            ))}
          </div>
        </div>
      )}
      {user?.is_admin && (
        <div className="px-3 py-3 border-t">
          <Link to="/dashboard" className="text-xs underline text-muted-foreground">{t('shell.backToUser')}</Link>
        </div>
      )}
    </div>
  );

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">{t('shell.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] border-r bg-card">
        <SidebarContent />
      </aside>

      {/* Mobile sheet (no trigger inside; controlled via state) */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-[280px]">
          <SheetHeader className="sr-only">
            <SheetTitle>{t('shell.menu')}</SheetTitle>
            <SheetDescription>{t('shell.navigate')}</SheetDescription>
          </SheetHeader>
          <SidebarContent />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-[260px]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b">
          <div className="flex items-center justify-between px-4 sm:px-6 h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
                <Menu size={22} />
              </Button>
              <div className="lg:hidden"><Logo size="sm" hideTextOnMobile /></div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <Badge className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 bg-primary border-0" data-testid="notifications-badge-count">
                        {unreadCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-2rem)] max-h-96 overflow-y-auto">
                  <DropdownMenuLabel className="space-y-2">
                    <div className="font-semibold">{t('shell.notifications')}</div>
                    <div className="flex flex-wrap gap-1">
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 text-xs"
                          disabled={notificationsBusy}
                          onClick={() => updateNotifications('readAll')}
                          data-testid="mark-all-read-button"
                        >
                          {t('shell.markRead')}
                        </Button>
                      )}
                      {notifications.length > 0 && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" disabled={notificationsBusy} onClick={() => updateNotifications('deleteAll')} data-testid="delete-all-notifications-button">
                          <Trash2 size={13} className="mr-1" />{t('shell.deleteAllNotifications')}
                        </Button>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifications.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t('shell.none')}</div>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id} className="flex items-center gap-1" data-testid={`notification-${n.id}`}>
                        <DropdownMenuItem className="min-w-0 flex-1 flex flex-col items-start gap-0.5 cursor-pointer" onClick={() => n.link && navigate(n.link)}>
                          <div className="flex items-center gap-2 w-full">
                            <span className={`text-sm font-medium ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {n.title}
                            </span>
                            {!n.read && <span className="ml-auto h-2 w-2 rounded-full bg-primary" />}
                          </div>
                          <span className="text-xs text-muted-foreground line-clamp-2">{n.body}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={notificationsBusy} className="shrink-0 p-2 text-muted-foreground focus:text-destructive cursor-pointer" onSelect={event => { event.preventDefault(); updateNotifications('delete', n.id); }} aria-label={`${t('shell.deleteNotification')} : ${n.title}`} title={t('shell.deleteNotification')} data-testid={`delete-notification-${n.id}`}>
                          <Trash2 size={15} aria-hidden="true" />
                        </DropdownMenuItem>
                      </div>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="profile-menu-button">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatar_url} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {(user.full_name || 'U').split(' ').map(n => n[0]).slice(0, 2).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">{user.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold">{user.full_name}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile-link">
                    <User className="mr-2 h-4 w-4" /> {t('shell.myProfile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile#roles')} data-testid="menu-manage-roles-link">
                    <Users className="mr-2 h-4 w-4" /> {t('profile.manageRoles')}
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin-link">
                      <BarChart3 className="mr-2 h-4 w-4" /> {t('shell.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" /> {t('shell.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 py-6 max-w-6xl mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
